import React from 'react';
import { connect } from 'react-redux';
import css from './SectionRecentBlogPosts.module.css';

// Exclude these asset names from blog posts
const EXCLUDED = ['landing-page', 'terms-of-service', 'privacy-policy'];

function extractBlogPostData(assetName, asset) {
  // Try to extract title, excerpt/description, date, and hero image
  const data = asset?.data || {};
  const meta = data.meta || {};
  // Try to find title: prefer meta.pageTitle, fallback to first heading field
  let title = meta.pageTitle?.content;
  if (!title && data.sections) {
    for (const section of data.sections) {
      if (section.title?.content) {
        title = section.title.content;
        break;
      }
    }
  }
  // Try to find excerpt/description: prefer meta.pageDescription, fallback to first markdown/text field
  let excerpt = meta.pageDescription?.content;
  if (!excerpt && data.sections) {
    for (const section of data.sections) {
      if (section.blocks) {
        for (const block of section.blocks) {
          if (block.text?.content) {
            excerpt = block.text.content;
            break;
          }
        }
      }
      if (excerpt) break;
    }
  }
  // Try to find date: prefer meta.published, fallback to meta.updated, fallback to null
  const date = meta.published || meta.updated || null;
  // Try to find hero image: look for meta.heroImage, or section/block heroImage
  let heroImage = null;
  if (meta.heroImage && meta.heroImage.variants) {
    // Pick largest variant
    const variants = Object.values(meta.heroImage.variants);
    heroImage = variants.sort((a, b) => (b.width || 0) - (a.width || 0))[0]?.url;
  } else if (data.sections) {
    for (const section of data.sections) {
      if (section.heroImage && section.heroImage.variants) {
        const variants = Object.values(section.heroImage.variants);
        heroImage = variants.sort((a, b) => (b.width || 0) - (a.width || 0))[0]?.url;
        break;
      }
      if (section.blocks) {
        for (const block of section.blocks) {
          if (block.heroImage && block.heroImage.variants) {
            const variants = Object.values(block.heroImage.variants);
            heroImage = variants.sort((a, b) => (b.width || 0) - (a.width || 0))[0]?.url;
            break;
          }
        }
      }
      if (heroImage) break;
    }
  }
  return {
    id: assetName,
    title: title || assetName,
    date,
    excerpt: excerpt || '',
    url: `/p/${assetName}`,
    heroImage,
  };
}

const SectionRecentBlogPosts = ({ pageAssetsData, inProgress, error }) => {
  // Filter and map blog posts
  const blogPosts = Object.entries(pageAssetsData || {})
    .filter(([name]) => !EXCLUDED.includes(name))
    .map(([name, asset]) => extractBlogPostData(name, asset))
    .filter(post => post.date) // Only show posts with a date
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  if (inProgress) {
    return (
      <div className={css.root}>
        <div className={css.loading}>Loading blog posts…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={css.root}>
        <div className={css.error}>Could not load blog posts.</div>
      </div>
    );
  }
  if (!blogPosts.length) {
    return (
      <div className={css.root}>
        <div className={css.empty}>No blog posts found.</div>
      </div>
    );
  }

  return (
    <div className={css.root}>
      <h2 className={css.heading}>Recent Blog Posts</h2>
      <ul className={css.list}>
        {blogPosts.map(post => (
          <li key={post.id} className={css.post}>
            {post.heroImage && (
              <div className={css.heroImageWrapper}>
                <img src={post.heroImage} alt={post.title} className={css.heroImage} />
              </div>
            )}
            <a href={post.url} className={css.title}>
              {post.title}
            </a>
            <div className={css.meta}>
              {post.date ? new Date(post.date).toLocaleDateString() : ''}
            </div>
            <div className={css.excerpt}>{post.excerpt}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

export default connect(mapStateToProps)(SectionRecentBlogPosts);
